import { useState } from "react";
import { orderMappingExportSelection } from "@/lib/manufacturing/orderMappingExport";
import {
  collectOrderMappingTags,
  gapHasOrderMappingTags,
  matchOrderMappingByChannel,
  orderMappingTagKey,
  orderMappingTagLabel,
  planOrderMappingBulkRules,
  skuRuleMaterialForGap,
  type OrderMappingTag,
} from "@/lib/manufacturing/orderMappingBulk";
import { getManufacturingMaterialUseDetail } from "@/lib/manufacturing/materialUsesClient";
import { upsertInventoryMatchRule, type InventoryOutlookReport } from "@/lib/manufacturing/inventoryMatchClient";
import { InventoryOutlookColorCrosswalk } from "./InventoryOutlookColorCrosswalk";
import { useOrderMappingSkuRules } from "./useOrderMappingSkuRules";
import { InventoryOutlookOrderMappingBulk, orderMappingBulkSummary } from "./InventoryOutlookOrderMappingBulk";
import { InventoryOutlookOrderMappingExport } from "./InventoryOutlookOrderMappingExport";
import { InventoryOutlookOrderMappingRow } from "./InventoryOutlookOrderMappingRow";

const inputClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900";

type Gap = NonNullable<InventoryOutlookReport["gaps"]>[number];

function gapKey(gap: Gap): string {
  return `${gap.unit_id}:${gap.material_use_id}:${gap.material_id}`;
}

function indicators(tags: readonly OrderMappingTag[]) {
  return tags.map((tag) => ({
    field: tag.field,
    op: tag.field === "addon_name" ? ("contains" as const) : ("equals" as const),
    value: tag.value,
  }));
}

export function InventoryOutlookOrderMapping({ report }: { report: InventoryOutlookReport | null }) {
  const gaps = report?.gaps ?? [];
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [checked, setChecked] = useState<string[]>([]);
  const [members, setMembers] = useState<Array<{ material_id: string; material_code: string; material_name: string }>>([]);
  const [materialId, setMaterialId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tagQuery, setTagQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<OrderMappingTag[]>([]);
  const [tagKeyByChannel, setTagKeyByChannel] = useState<Record<string, string>>({});
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const { skuRules, addSkuRules, removeSkuRule } = useOrderMappingSkuRules(setError);

  function channelFor(gap: Gap): string {
    return gap.finish_channel?.trim() ?? "";
  }

  function covered(gap: Gap): boolean {
    return saved.includes(gapKey(gap)) || skuRuleMaterialForGap(gap, skuRules) !== null;
  }

  const textVisible = gaps.filter((gap) => {
    if (covered(gap)) return false;
    const hay = `${gap.serial} ${gap.use_code} ${gap.material_code} ${gap.material_name}`.toLowerCase();
    return hay.includes(query.trim().toLowerCase());
  });
  const visible = textVisible.filter((gap) => gapHasOrderMappingTags(gap, selectedTags));
  const pickedGaps = gaps.filter((gap) => picked.includes(gapKey(gap)));
  const exportGaps = orderMappingExportSelection(visible, pickedGaps);
  const shownPicked = visible.length > 0 && visible.every((gap) => picked.includes(gapKey(gap)));
  const selectedKeys = new Set(selectedTags.map(orderMappingTagKey));
  const suggestions = collectOrderMappingTags(visible).filter((tag) => {
    if (selectedKeys.has(orderMappingTagKey(tag))) return false;
    const needle = tagQuery.trim().toLowerCase();
    return orderMappingTagLabel(tag).toLowerCase().includes(needle);
  });
  const bulkPlans = planOrderMappingBulkRules(
    visible.map((gap) => ({ key: gapKey(gap), material_use_id: gap.material_use_id, use_code: gap.use_code })),
    chosen,
  );

  function membersFor(gap: Gap) {
    if (selectedTags.length === 0 && active === gapKey(gap)) {
      return members.map((member) => ({ ...member, finish_code: null }));
    }
    const option = report?.finish_options?.find((row) => row.material_use_id === gap.material_use_id);
    return (option?.members ?? []).map((member) => ({
      material_id: member.material_id,
      material_code: member.material_code,
      material_name: member.material_name || member.finish_code || member.material_id,
      finish_code: member.finish_code,
    }));
  }

  function materialLabel(id: string): string {
    for (const option of report?.finish_options ?? []) {
      const member = option.members?.find((row) => row.material_id === id);
      if (member) return [member.material_code, member.material_name].filter(Boolean).join(" — ") || id;
    }
    return id;
  }

  function addTag(tag: OrderMappingTag) {
    setSelectedTags((prev) => (prev.some((item) => orderMappingTagKey(item) === orderMappingTagKey(tag)) ? prev : [...prev, tag]));
    setTagQuery("");
    setChosen({});
    setReasons({});
  }

  function removeTag(tag: OrderMappingTag) {
    const key = orderMappingTagKey(tag);
    setSelectedTags((prev) => prev.filter((item) => orderMappingTagKey(item) !== key));
    setTagKeyByChannel((prev) => Object.fromEntries(Object.entries(prev).filter(([, tagKey]) => tagKey !== key)));
    setChosen({});
    setReasons({});
  }

  function hideUse(useId: string) {
    setSaved((prev) => [...new Set([...prev, ...gaps.filter((gap) => gap.material_use_id === useId).map(gapKey)])]);
  }

  const channelCounts = new Map<string, number>();
  for (const gap of visible) channelCounts.set(channelFor(gap), (channelCounts.get(channelFor(gap)) ?? 0) + 1);
  const channelRows = [...channelCounts.entries()]
    .map(([channel, count]) => ({ channel, count }))
    .sort((a, b) => b.count - a.count || a.channel.localeCompare(b.channel));

  async function openGap(gap: Gap) {
    if (selectedTags.length > 0) return;
    const key = gapKey(gap);
    setActive(key);
    setChecked(gap.aspects.map((row) => orderMappingTagKey(row)));
    setMaterialId("");
    setError(null);
    const detail = await getManufacturingMaterialUseDetail(gap.material_use_id, { active_only: true });
    if (!detail.ok) {
      setMembers([]);
      setError(detail.error);
      return;
    }
    setMembers(
      detail.data.members.map((member) => ({
        material_id: member.material_id,
        material_code: member.material_code,
        material_name: member.material_name,
      })),
    );
  }

  function matchFinishes() {
    const matched = matchOrderMappingByChannel({
      gaps: visible.map((gap) => ({ key: gapKey(gap), channel: channelFor(gap), members: membersFor(gap) })),
      tags: selectedTags,
      tagKeyByChannel,
    });
    const next: Record<string, string> = {};
    const why: Record<string, string> = {};
    for (const [key, result] of Object.entries(matched)) {
      why[key] = result.reason;
      if (result.material_id) next[key] = result.material_id;
    }
    setChosen(next);
    setReasons(why);
  }

  async function saveRule(gap: Gap, material: string, tags: readonly OrderMappingTag[]) {
    if (!material || tags.length === 0) {
      setError("Pick a SKU and at least one order tag before saving.");
      return false;
    }
    const savedRule = await upsertInventoryMatchRule({
      material_use_id: gap.material_use_id,
      material_id: material,
      priority: 100,
      indicators: indicators(tags),
    });
    if (!savedRule.ok) {
      setError(savedRule.error);
      return false;
    }
    addSkuRules([savedRule.data.rule]);
    hideUse(gap.material_use_id);
    return true;
  }

  async function save(gap: Gap) {
    const tags = selectedTags.length > 0 ? selectedTags : gap.aspects.filter((row) => checked.includes(orderMappingTagKey(row)));
    const material = selectedTags.length > 0 ? chosen[gapKey(gap)] ?? "" : materialId;
    setBusy(true);
    setError(null);
    const ok = await saveRule(gap, material, tags);
    setBusy(false);
    if (!ok) return;
    setActive(null);
    setNote("Rule saved. Generate the outlook again and this part will follow the order instead of the default SKU.");
  }

  async function saveAll() {
    if (selectedTags.length === 0 || bulkPlans.plans.length === 0) return;
    setBusy(true);
    setError(null);
    for (const plan of bulkPlans.plans) {
      const gap = visible.find((row) => row.material_use_id === plan.material_use_id);
      if (!gap) continue;
      const ok = await saveRule(gap, plan.material_id, selectedTags);
      if (!ok) {
        setBusy(false);
        return;
      }
    }
    setBusy(false);
    setNote("Rules saved. Generate the outlook again and these part uses follow the selected tags.");
  }

  if (!report) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">Generate the outlook first. Unresolved parts show up here.</p>;
  }

  return (
    <div className="space-y-3">
      <InventoryOutlookColorCrosswalk
        report={{ ...report, gaps: (report.gaps ?? []).filter((gap) => !covered(gap)) }}
        skuRules={skuRules}
        onRemoveSkuRule={(id) => void removeSkuRule(id)}
        onSkuRulesSaved={addSkuRules}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <input
              className={`${inputClass} min-w-[16rem] flex-1`}
              value={query}
              placeholder="Filter by serial or part"
              aria-label="Filter unresolved parts"
              onChange={(e) => setQuery(e.target.value)}
            />
            <label className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={shownPicked}
                disabled={visible.length === 0}
                onChange={() => {
                  const keys = visible.map(gapKey);
                  setPicked((prev) => (shownPicked ? prev.filter((key) => !keys.includes(key)) : [...new Set([...prev, ...keys])]));
                }}
              />
              Select shown
            </label>
            <InventoryOutlookOrderMappingExport gaps={exportGaps} label={pickedGaps.length > 0 ? `Export ${pickedGaps.length}` : "Export"} />
          </div>
        }
      />
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        One-off rules name a single SKU. Filter by tags, point each channel at the tag that picks its finish, then save the group. Channel changes open that part use in Models.
      </p>
      <InventoryOutlookOrderMappingBulk
        tagQuery={tagQuery}
        suggestions={suggestions}
        selected={selectedTags}
        channels={channelRows}
        tagKeyByChannel={tagKeyByChannel}
        summary={orderMappingBulkSummary(selectedTags, bulkPlans.plans, materialLabel)}
        skipped={bulkPlans.skipped}
        busy={busy}
        canSave={selectedTags.length > 0 && bulkPlans.plans.length > 0}
        onTagQuery={setTagQuery}
        onAdd={addTag}
        onRemove={removeTag}
        onAssignChannel={(channel, tagKey) => {
          setTagKeyByChannel((prev) => ({ ...prev, [channel]: tagKey }));
          setChosen({});
          setReasons({});
        }}
        onMatch={matchFinishes}
        onSaveAll={() => void saveAll()}
      />
      {gaps.length === 0 ? <p className="text-sm text-gray-500 dark:text-zinc-400">Nothing on this outlook is waiting for a one-off SKU.</p> : null}
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {note ? <p className="text-sm text-gray-700 dark:text-zinc-300">{note}</p> : null}
      <ul className="max-h-[32rem] space-y-2 overflow-y-auto">
        {visible.map((gap) => {
          const key = gapKey(gap);
          return (
            <InventoryOutlookOrderMappingRow
              key={key}
              gap={gap}
              picked={picked.includes(key)}
              open={selectedTags.length > 0 || active === key}
              channel={channelFor(gap)}
              busy={busy}
              selectedTags={selectedTags}
              checked={checked}
              members={membersFor(gap)}
              materialId={selectedTags.length > 0 ? chosen[key] ?? "" : materialId}
              matchReason={reasons[key] ?? ""}
              onTogglePicked={() => setPicked((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]))}
              onOpen={() => void openGap(gap)}
              onAddTag={addTag}
              onToggleChecked={(id) => setChecked((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))}
              onMaterial={(id) => {
                if (selectedTags.length > 0) setChosen((prev) => ({ ...prev, [key]: id }));
                else setMaterialId(id);
              }}
              onSave={() => void save(gap)}
            />
          );
        })}
      </ul>
    </div>
  );
}
